<template>
  <div class="create-credentials">
    <loading-credentials v-if="finishedAddingCredentials && !loadingTimeoutReached"/>
    <async-fallback v-else-if="finishedAddingCredentials && loadingTimeoutReached"/>
    <template v-else>
      <div class="title">היי {{ householdName }},</div>
      <div class="intro">כדי לבנות בשבילך תזרים אוטומטי מדויק, המערכת צריכה את פרטי המשתמש של חשבונות הבנק וחשבונות האשראי.
        <span class="why" v-on:click="openTrust">לחצו לקרוא מדוע.</span></div>
      <credentials-list></credentials-list>
    </template>

    <transition name="fade">
      <trust-modal v-if="trustOpened" :on-modal-closed="closeTrust"/>
    </transition>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import Segment from '../../../../../../Segment';
import TrustModal from './TrustModal.vue';
import CredentialsList from '../../../../../../base-components/credentials/credentials-list/CredentialsList.vue';
import ShowUnsupportedModalMixin from '../../../unsupported-banks/ShowUnsupportedModalMixin.vue';
import LoadingCredentials from './LoadingCredentials.vue';
import AsyncFallback from './AsyncFallback.vue';
import router from '../../../../../../router';

export default {
  name: 'AddCredentialsOnboarding',
  mixins: [ShowUnsupportedModalMixin],
  async created() {
    this.startLoading(this.$options.name);
    await this.fetchCredentials();
    this.doneLoading(this.$options.name);
    Segment.trackUserGot('CreateCredentialsEntered');
    window.scrollTo(0, 0);
  },
  data() {
    return {
      trustOpened: false,
      loadingTimeoutReached: false,
      loadingStartTime: null,
    };
  },
  components: {
    TrustModal,
    CredentialsList,
    LoadingCredentials,
    AsyncFallback,
  },
  computed: {
    ...mapState('credentials', ['loading', 'credentialsList', 'credsToAccounts']),
    ...mapState('websocket', ['socketMessage']),
    ...mapState('applicationState', ['cashflowState', 'notificationState']),
    ...mapGetters('credentials', ['invalidCredentialsList', 'newCredentialsList',
      'validCredentialsList', 'discountPendingCredentialsList', 'hasCredentials']),
    ...mapGetters('session', ['householdName']),
    ...mapGetters('applicationState', ['notificationTypes', 'cashflowTypes']),
    credentialsToCreate() {
      return [...this.invalidCredentialsList, ...this.newCredentialsList, ...this.discountPendingCredentialsList];
    },
    sortedCredentialsList() {
      return _.sortBy(this.credentialsList, c => c.status);
    },
    finishedAddingCredentials() {
      const finished = this.hasCredentials && _.isEmpty(this.credentialsToCreate) && !this.loading;
      if (finished) {
        Segment.trackUserGot('OnboardDoneEntered');
      }
      return finished;
    },
  },
  methods: {
    ...mapActions('credentials', ['createCredentials', 'fetchCredentials', 'fetchCredsToAccounts']),
    ...mapActions('loader', ['startLoading', 'doneLoading']),
    ...mapActions('applicationState', ['initApplicationState']),
    async createCredentialsCard({ source }) {
      if (source.type === 'bank') {
        Segment.trackUserInteraction('BankNameClicked', {
          name: source.sourceName,
        });
      } else {
        Segment.trackUserInteraction('CardNameClicked', {
          name: source.sourceName,
        });
      }
      if (source.unsupported) {
        this.showUnsupportedPopup(source);
      } else {
        await this.createCredentials({ sourceName: source.sourceName });
      }
    },
    async allAccountsUpdated() {
      await this.fetchCredsToAccounts();
      const credsIds = _.map(this.credentialsList, 'credentialsId');
      const versionCredIds = _.map(this.credsToAccounts, 'credentialsId');
      return _.every(credsIds, credsId => _.includes(versionCredIds, credsId));
    },
    openTrust() {
      this.trustOpened = true;
    },
    closeTrust() {
      this.trustOpened = false;
    },
    setLoadingTimeout() {
      this.loadingStartTime = moment().toDate();
      setTimeout(() => {
        this.loadingTimeoutReached = true;
      }, 60 * 1000);
    },
    redirectToCashflow() {
      router.push({ path: '/home' });
    },
    async versionForCredsFinished() {
      return this.finishedAddingCredentials && !this.loadingTimeoutReached
        && await this.allAccountsUpdated();
    },
  },
  watch: {
    async socketMessage(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (await this.versionForCredsFinished()) {
          const loadingTimeSeconds = moment().diff(this.loadingStartTime, 'second');
          Segment.trackUserGot('SyncAddCredentialsSuccess', { loadingTimeSeconds });
          this.redirectToCashflow();
        }
      }
    },
    finishedAddingCredentials(newValue) {
      if (newValue) {
        this.setLoadingTimeout();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../scss/mixins';

  .create-credentials {
    margin: 30px 0;
    display: flex;
    flex-direction: column;
    align-items: center;

    .title {
      @include title;
      @include responsive-padding;
      display: inline-block;
      width: 100%;
    }

    .intro {
      @include responsive-padding;
      display: inline-block;
      width: 100%;
      font-size: 20px;
      margin: 4px 0 26px;
      line-height: 1.15;

      .why {
        font-weight: bold;
        text-decoration: underline;
        color: $riseup_blue;
      }
    }

    .margin-bottom {
      margin-bottom: 25px;
    }

    .fade-enter-active, .fade-leave-active {
      transition: opacity .5s;
    }
    .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
      opacity: 0;
    }
  }

</style>
